export type Locale = "de" | "en";

export const locales: readonly Locale[] = ["de", "en"];

export const isLocale = (candidate: string): candidate is Locale =>
  locales.includes(candidate as Locale);

/**
 * The single shape every dictionary must satisfy. Because de.ts and en.ts use
 * `satisfies Dictionary`, a missing or misspelled key is a compile error.
 */
export type Dictionary = {
  app: {
    name: string;
    language: string;
    localeName: Record<Locale, string>;
  };
  nav: {
    dashboard: string;
    catalog: string;
    products: string;
    personas: string;
    questionnaires: string;
    authors: string;
    views: string;
    overview: string;
    compare: string;
    reports: string;
    customers: string;
    start: string;
    persona: string;
    logout: string;
    collapse: string;
    expand: string;
  };
  auth: {
    title: string;
    token: string;
    signIn: string;
    signingIn: string;
    refused: string;
  };
  preview: {
    viewing: string;
    exit: string;
    readOnly: string;
    enter: string;
  };
  customers: {
    title: string;
    newCustomer: string;
    users: string;
    access: string;
    history: string;
    team: string;
    teamIntro: string;
    products: string;
    productsHint: string;
    withoutData: string;
    assign: string;
    unassign: string;
    nothingAssigned: string;
    noneFree: string;
    openQuestionnaires: string;
    newestReport: string;
    userCount: string;
    inactive: string;
    relevancyQuestionnaires: string;
    notFound: string;
  };
  users: {
    newUser: string;
    role: string;
    roleAdmin: string;
    roleCustomer: string;
    author: string;
    authorHint: string;
    tokenPrefix: string;
    lastSeen: string;
    never: string;
    issuedTitle: string;
    issuedIntro: string;
    understood: string;
    rotate: string;
    rotateHint: string;
    revoke: string;
    revoked: string;
    none: string;
  };
  portal: {
    startTitle: string;
    open: string;
    noOpen: string;
    fill: string;
    reports: string;
    noReports: string;
    compare: string;
    compareHint: string;
    empty: string;
    draft: string;
    lockedOn: string;
    submittedOn: string;
    personaTitle: string;
    ratings: string;
    noRatings: string;
    ratingsOpen: string;
    requirements: string;
    noRequirements: string;
    fillRelevancy: string;
    compareFor: string;
    noPersona: string;
    noWeighting: string;
    noProducts: string;
  };
  common: {
    of: string;
    optional: string;
    retry: string;
    loading: string;
    saving: string;
    saved: string;
    saveFailed: string;
    notAssessed: string;
    coverage: string;
    score: string;
    band: string;
    answer: string;
    yes: string;
    no: string;
    dontKnow: string;
    none: string;
    all: string;
    create: string;
    save: string;
    cancel: string;
    copy: string;
    copied: string;
    edit: string;
    add: string;
    new: string;
    empty: string;
    identity: string;
    name: string;
    title: string;
    label: string;
    description: string;
    active: string;
    status: string;
    product: string;
    topic: string;
    capability: string;
    question: string;
    persona: string;
    author: string;
    weight: string;
    actions: string;
    resizeColumn: string;
    resizeColumnHint: string;
    notAvailable: string;
    customer: string;
    customerHint: string;
    internal: string;
    noCustomer: string;
  };
  band: {
    missing: string;
    partial: string;
    solid: string;
    excellent: string;
  };
  error: {
    network: string;
    decode: string;
    notFound: string;
    validation: string;
    conflict: string;
    inUse: string;
    unauthorized: string;
    forbidden: string;
    internal: string;
  };
  remove: {
    action: string;
    title: string;
    confirm: string;
    deleting: string;
    blockedTitle: string;
    blockedIntro: string;
    cascadeWarning: string;
    detachNote: string;
    forceLabel: string;
    forceHint: string;
    forceConfirm: string;
    done: string;
  };
  answer: {
    sourceHuman: string;
    sourceAi: string;
    confidenceHigh: string;
    confidenceLow: string;
    history: string;
    validFrom: string;
    validTo: string;
    value: string;
    noAnswers: string;
  };
  dashboard: {
    title: string;
    questionnaires: string;
    changes: string;
    entity: string;
    actor: string;
    at: string;
    scores: string;
    scoresFromLocked: string;
    scoresFromLive: string;
    noEvaluation: string;
  };
  compare: {
    title: string;
    productA: string;
    productB: string;
    swap: string;
    level: string;
    levelTopic: string;
    levelCapability: string;
    axes: string;
    allAxes: string;
    noAxes: string;
    noAxesAvailable: string;
    noAxesPicked: string;
    reset: string;
    samePair: string;
    tableCaption: string;
    view: string;
    viewHint: string;
    newestReport: string;
    sourceView: string;
    tooFew: string;
    differences: string;
    differencesIntro: string;
    gap: string;
    leads: string;
    noDifferences: string;
    tiedCount: string;
    oneSidedCount: string;
    partsCapabilities: string;
    partsQuestions: string;
    noParts: string;
    partTied: string;
    partOneSided: string;
    persona: string;
    personaNone: string;
    personaHint: string;
    weightedFor: string;
    weightedUp: string;
    weightedDown: string;
    weightedLevel: string;
    weightHeader: string;
    weightOf: string;
    weightedGap: string;
    weightedGapKey: string;
    notRated: string;
    explanation: string;
    showExplanation: string;
    noExplanation: string;
    noAnswer: string;
    comment: string;
    answeredAs: string;
    inCapability: string;
  };
  history: {
    title: string;
    link: string;
    at: string;
    actor: string;
    change: string;
    asOfJump: string;
    noEvents: string;
    created: string;
    updated: string;
    deleted: string;
    translation: string;
    draftAnswer: string;
    unknown: string;
  };
  tree: {
    selectAll: string;
    selected: string;
    expand: string;
    collapse: string;
    expandAll: string;
    collapseAll: string;
    clear: string;
    unassigned: string;
    empty: string;
  };
  coverage: {
    label: Record<"counted" | "outside" | "unanswered", string>;
    hint: Record<"counted" | "outside" | "unanswered", string>;
    view: string;
    viewHint: string;
    noView: string;
  };
  views: {
    title: string;
    newView: string;
    topics: string;
    capabilities: string;
    personas: string;
    products: string;
    questionnaires: string;
    allHint: string;
    hollow: string;
    narrows: string;
    everything: string;
    none: string;
  };
  overview: {
    title: string;
    caveat: string;
    topicAverage: string;
    average: string;
    answeredOf: string;
    origin: string;
    noAnswerYet: string;
    unassigned: string;
    unassignedHint: string;
    recorded: string;
    onlyAnswered: string;
    onlyAnsweredHint: string;
    noProducts: string;
    empty: string;
  };
  catalog: {
    title: string;
    relevancy: string;
    capabilities: string;
    questions: string;
    questionsLinked: string;
    informational: string;
    newTopic: string;
    newCapability: string;
    newQuestion: string;
    answerType: string;
    linkedQuestions: string;
    linkedQuestionsHint: string;
    topicQuestions: string;
    topicQuestionsHint: string;
    capabilityDescriptionHint: string;
    owningTopicHint: string;
    questionTopicHint: string;
    questionTopicRequired: string;
    unassignedCapabilities: string;
    unassignedCapabilitiesHint: string;
    danglingTopic: string;
    capabilityTopicRequired: string;
    compareAnswers: string;
    compareAnswersHint: string;
    search: string;
    searchHint: string;
    searchPlaceholder: string;
    noMatches: string;
  };
  products: {
    title: string;
    newProduct: string;
    bandOverview: string;
    chooseView: string;
    viewHint: string;
    references: string;
    overview: string;
    answers: string;
    dossier: string;
    details: string;
    tags: string;
    tagsHint: string;
    tagsLabel: string;
    addTag: string;
    removeTag: string;
    noTags: string;
    noReferences: string;
    noDescription: string;
    noDossier: string;
    editDossier: string;
    dossierHint: string;
    write: string;
    preview: string;
    unsaved: string;
    editDetails: string;
  };
  personas: {
    title: string;
    newPersona: string;
    requirements: string;
    newRequirement: string;
    minimumKind: string;
    minimumScore: string;
    minimumBand: string;
    rationale: string;
    rationaleHint: string;
    rationaleRequired: string;
    view: string;
  };
  authors: {
    title: string;
    newAuthor: string;
    email: string;
    comment: string;
  };
  questionnaires: {
    title: string;
    workbook: string;
    workbookHint: string;
    workbookProduct: string;
    newQuestionnaire: string;
    scope: string;
    scopeHint: string;
    productHint: string;
    noProduct: string;
    noAuthors: string;
    addAuthor: string;
    answers: string;
    answersHint: string;
    noAnswers: string;
    magicLink: string;
    copyLink: string;
    review: string;
    accept: string;
    accepted: string;
    drafts: string;
    noDrafts: string;
    statusPending: string;
    statusReceived: string;
    statusAnswered: string;
    statusEvaluated: string;
    created: string;
  };
  relevancy: {
    title: string;
    ratings: string;
    noRatings: string;
    rating: string;
    note: string;
    weight: string;
    /** The merged column: step and weight read as one mark. */
    weighting: string;
    newQuestionnaire: string;
    scope: string;
    scopeHint: string;
    magicLink: string;
    copyLink: string;
    accept: string;
    accepted: string;
    review: string;
    questionnaires: string;
    noQuestionnaires: string;
    noAuthors: string;
    scale: Record<
      "much_less" | "less" | "equal" | "more" | "much_more",
      string
    >;
  };
  relevancyFill: {
    title: string;
    noteTitle: string;
    noteWhat: string;
    noteWhy: string;
    noteHow: string;
    greeting: string;
    progressTitle: string;
    setAll: string;
    addNote: string;
    note: string;
    noteHint: string;
    submit: string;
    submitting: string;
    submitHint: string;
    confirmSubmit: string;
    confirmYes: string;
    loadFailed: string;
    noCapabilities: string;
    skipToCapabilities: string;
  };
  reports: {
    title: string;
    downloadWorkbook: string;
    builder: string;
    view: string;
    primaryQuestionnaire: string;
    mergeOrder: string;
    mergeOrderHint: string;
    knockoutMode: string;
    knockoutEnforce: string;
    knockoutAnnotate: string;
    knockoutIgnore: string;
    asOf: string;
    asOfHint: string;
    evaluateNow: string;
    persist: string;
    lock: string;
    locked: string;
    live: string;
    ranking: string;
    perTopic: string;
    personaRankings: string;
    violations: string;
    openRequirements: string;
    contributing: string;
    minimum: string;
    actual: string;
    excluded: string;
    requirementsSkipped: string;
    formulaVersion: string;
    weightedScore: string;
    globalScore: string;
    reorderedByRatings: string;
    reorderedHint: string;
    appliedWeights: string;
    capabilities: string;
    drillDown: string;
    noReport: string;
    noProducts: string;
    noAnswers: string;
    status: string;
    preview: string;
    previewHint: string;
    saved: string;
  };
  fill: {
    title: string;
    greeting: string;
    progressTitle: string;
    progressAnswered: string;
    informational: string;
    product: string;
    productMissing: string;
    comment: string;
    commentHint: string;
    explanation: string;
    explanationHint: string;
    dontKnowHint: string;
    ratingHint: string;
    textHint: string;
    multiSelectHint: string;
    submit: string;
    submitting: string;
    submitHint: string;
    loadFailed: string;
    noQuestions: string;
    skipToQuestions: string;
  };
  dead: {
    unavailableTitle: string;
    unavailableBody: string;
    submittedTitle: string;
    submittedBody: string;
  };
};
